import React, { useState } from 'react';
import { Container, Tab, Tabs, Box, Typography, Paper, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import GeneralLedger from '../components/accounting/GeneralLedger';
import ProfitLoss from '../components/accounting/ProfitLoss';
import BalanceSheet from '../components/accounting/BalanceSheet';
import TrialBalance from '../components/accounting/TrialBalance';
import ChartOfAccounts from '../components/accounting/ChartOfAccounts';
import CashflowStatement from '../components/accounting/CashflowStatement';
import GsecBalanceSheet from '../components/accounting/GsecBalanceSheet';
import { useTheme } from '@mui/material/styles';
import PortfolioMasterPage from './PortfolioMasterPage';
import StrategyMasterPage from './StrategyMasterPage';

// Use a default color scheme if styleGuide is not available
const colors = {
  darkBackground: '#f5f5f5',
  lightBackground: '#ffffff'
};

const AdminPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const theme = useTheme();
  const navigate = useNavigate();
  // Get user and allowedTabs from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';
  const allowedTabs = isAdmin ? ['portfolio_master'] : (user.allowed_tabs || []);
  const showPortfolioTab = isAdmin || allowedTabs.includes('portfolio_master');
  const showStrategyTab = isAdmin || allowedTabs.includes('strategy_master');

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Container maxWidth="xl" className="ui-page admin-page-shell" sx={{ mt: 4, mb: 4 }}>
      <Paper 
        className="ui-card"
        elevation={3} 
        sx={{ 
          p: 3, 
          borderRadius: 2,
          backgroundColor: theme.palette.mode === 'dark' ? colors.darkBackground : 'var(--bg-card)',
          border: '1px solid var(--border)',
          boxShadow: 'var(--shadow-sm)'
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
          Financial Management
        </Typography>
        {isAdmin && (
          <Button
            variant="contained"
            color="primary"
            sx={{ mb: 3 }}
            onClick={() => navigate('/admin/users')}
          >
            User Management (Authorize Tabs)
          </Button>
        )}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            aria-label="financial management tabs"
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                fontWeight: 'bold',
                fontSize: '1rem',
                textTransform: 'none',
              },
            }}
          >
            <Tab label="General Ledger" id="tab-0" />
            <Tab label="Profit & Loss" id="tab-1" />
            <Tab label="Balance Sheet" id="tab-2" />
            <Tab label="Trial Balance" id="tab-3" />
            <Tab label="Chart of Accounts" id="tab-4" />
            <Tab label="Cashflow Statement" id="tab-5" />
            <Tab label="GSec Balance Sheet" id="tab-6" />
            {showPortfolioTab && <Tab label="Portfolio Master" id="tab-7" />}
            {showStrategyTab && <Tab label="Strategy Master" id="tab-8" />} {/* Add Strategy Master tab */}
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          <GeneralLedger />
        </TabPanel>
        <TabPanel value={activeTab} index={1}>
          <ProfitLoss />
        </TabPanel>
        <TabPanel value={activeTab} index={2}>
          <BalanceSheet />
        </TabPanel>
        <TabPanel value={activeTab} index={3}>
          <TrialBalance />
        </TabPanel>
        <TabPanel value={activeTab} index={4}>
          <ChartOfAccounts />
        </TabPanel>
        <TabPanel value={activeTab} index={5}>
          <CashflowStatement />
        </TabPanel>
        <TabPanel value={activeTab} index={6}>
          <GsecBalanceSheet />
        </TabPanel>
        {showPortfolioTab && (
          <TabPanel value={activeTab} index={7}>
            <PortfolioMasterPage />
          </TabPanel>
        )}
        {showStrategyTab && (
          <TabPanel value={activeTab} index={8}>
            <StrategyMasterPage />
          </TabPanel>
        )}
      </Paper>
    </Container>
  );
};

// TabPanel component for tab content
const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

export default AdminPage;
